import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { WelcomeModal } from '../../components/modals';

// Zod validation schema for stat takers
const statTakerSchema = z.object({
  firstName: z.string().min(1, 'First name is required').trim(),
  lastName: z.string().min(1, 'Last name is required').trim(),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  phone: z.string().min(1, 'Phone number is required').trim(),
});

const statTakersFormSchema = z.object({
  statTakers: z.array(statTakerSchema).min(1, 'At least one stat taker is required'),
});

type StatTakersFormData = z.infer<typeof statTakersFormSchema>;

const StatTakers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSending, setIsSending] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>('/images/logo-notext.png');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [clientName, setClientName] = useState<string>('');

  // Check if user was redirected here after invitation acceptance
  const isNewUser = searchParams.get('welcome') === 'true' || searchParams.get('new') === 'true';

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<StatTakersFormData>({
    resolver: zodResolver(statTakersFormSchema),
    defaultValues: {
      statTakers: [
        { firstName: '', lastName: '', email: '', phone: '' },
        { firstName: '', lastName: '', email: '', phone: '' },
        { firstName: '', lastName: '', email: '', phone: '' },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'statTakers',
  });

  const watchedStatTakers = watch('statTakers');

  // Fetch client info and show welcome modal for new users
  useEffect(() => {
    const clientId = sessionManager.getClientId();
    if (!clientId) return;

    const fetchClientInfo = async () => {
      try {
        const res = await api.get(`${API_URLS.GET_CLIENT_BY_ID}`, {
          params: { clientId },
        });
        if (res.data?.success) {
          const data = res.data.data || {};
          const img: string | undefined = data.image;
          const fullPath = img
            ? `${process.env.REACT_APP_UPLOAD_URL}uploads/clients/${img}`
            : '/images/logo-notext.png'; // fallback to StatDog logo
          setLogoUrl(fullPath);
          setClientName(data.name || '');

          // Show welcome modal for new users
          if (isNewUser) {
            setShowWelcomeModal(true);
          }
        }
      } catch (error) {
        // Keep default StatDog logo on error
        console.error('Error fetching client info:', error);
        // Still show welcome modal for new users even if client fetch fails
        if (isNewUser) {
          setShowWelcomeModal(true);
        }
      }
    };

    fetchClientInfo();
  }, [isNewUser]);

  const addStatTaker = () => {
    append({ firstName: '', lastName: '', email: '', phone: '' });
  };

  const removeStatTaker = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const onSubmit = async (data: StatTakersFormData) => {
    setIsSending(true);
    try {
      // Filter out empty stat takers
      const validStatTakers = data.statTakers.filter(
        (st) => st.firstName.trim() && st.lastName.trim() && st.email.trim(),
      );

      if (validStatTakers.length === 0) {
        showErrorMsg('Please add at least one complete stat taker');
        return;
      }

      const response = await api.post(API_URLS.ADD_STAT_TAKERS, {
        statTakers: validStatTakers,
      });

      if (response.data.success) {
        showSuccessMsg(
          `Successfully sent invitations to ${
            validStatTakers.length
          } stat taker${validStatTakers.length > 1 ? 's' : ''}!`,
        );
        navigate('/users');
      } else {
        showErrorMsg(response.data.message || 'Failed to send invitations');
      }
    } catch (error: any) {
      console.error('Error sending stat taker invitations:', error);
      showErrorMsg(error.response?.data?.message || 'Failed to send invitations. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const hasValidStatTakers = watchedStatTakers.some(
    (st) => st.firstName.trim() && st.lastName.trim() && st.email.trim(),
  );

  const handleSkipForNow = () => {
    navigate('/players');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center">
            {/* Skip button for coaches who want to set this up later */}
            <div className="flex justify-end mb-4">
              <button onClick={handleSkipForNow} className="text-gray-500 hover:text-gray-700 text-sm underline">
                Skip for now
              </button>
            </div>

            <div className="flex justify-center mb-4">
              <img
                src={logoUrl}
                alt="Organization Logo"
                className="h-20 w-20 object-contain"
                onError={(e) => {
                  // Fallback to StatDog logo if client logo fails to load
                  e.currentTarget.src = '/images/logo-notext.png';
                }}
              />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">Add Your Data Capture Team</h1>
            <h2 className="text-xl text-green-600 font-semibold mb-4">Stat Takers – the STATDOGs!</h2>
            <div className="max-w-3xl mx-auto text-gray-700 leading-relaxed">
              <p className="mb-4">
                We recommend you recruit at least <strong>three parents or managers per game</strong>. Please provide
                their contact information and we'll take it from here!
              </p>
              <p className="text-sm bg-green-50 border-l-4 border-green-400 p-4 rounded">
                We will contact them and train them in how to capture with the tap of a button the team and player stats
                you need each game <strong>Real-Time!</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">💡 Pro Tips</h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Recruit enthusiastic parents who attend games regularly</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Having 3+ stat takers ensures coverage if someone can't make a game</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>We'll provide comprehensive training materials and support</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Stat takers can start capturing data immediately after training</span>
            </li>
          </ul>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-6">
              {fields.map((field, index) => (
                <div key={field.id} className="border border-gray-200 rounded-lg p-6 relative">
                  {/* Remove button */}
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStatTaker(index)}
                      className="absolute top-4 right-4 text-red-500 hover:text-red-700 transition-colors"
                      title="Remove stat taker"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}

                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Stat Taker #{index + 1}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-1">First Name *</label>
                      <input
                        type="text"
                        {...register(`statTakers.${index}.firstName`)}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors.statTakers?.[index]?.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={isSending}
                      />
                      {errors.statTakers?.[index]?.firstName && (
                        <p className="text-red-500 text-sm mt-1">{errors.statTakers[index]?.firstName?.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Last Name *</label>
                      <input
                        type="text"
                        {...register(`statTakers.${index}.lastName`)}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors.statTakers?.[index]?.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={isSending}
                      />
                      {errors.statTakers?.[index]?.lastName && (
                        <p className="text-red-500 text-sm mt-1">{errors.statTakers[index]?.lastName?.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Email *</label>
                      <input
                        type="email"
                        {...register(`statTakers.${index}.email`)}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors.statTakers?.[index]?.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={isSending}
                      />
                      {errors.statTakers?.[index]?.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.statTakers[index]?.email?.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-gray-700 font-medium mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        {...register(`statTakers.${index}.phone`)}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors.statTakers?.[index]?.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={isSending}
                      />
                      {errors.statTakers?.[index]?.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.statTakers[index]?.phone?.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Stat Taker Button */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={addStatTaker}
                  className="inline-flex items-center px-4 py-2 border-2 border-dashed border-green-300 rounded-lg text-green-600 hover:text-green-700 hover:border-green-400 transition-colors font-medium"
                  disabled={isSending}
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Add Stat Taker
                </button>
              </div>

              {/* Submit Button */}
              <div className="border-t pt-6">
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={!hasValidStatTakers || isSending}
                    className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                  >
                    {isSending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Sending Invitations...
                      </>
                    ) : (
                      'Send Invites to Your Stat Takers'
                    )}
                  </button>

                  {!hasValidStatTakers && (
                    <p className="text-sm text-gray-500 mt-2">
                      Please complete at least one stat taker to send invitations
                    </p>
                  )}
                </div>
              </div>

              {/* Additional Actions */}
              <div className="border-t pt-6 mt-8">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">You can always add more stat takers later.</p>
                  <button
                    type="button"
                    onClick={handleSkipForNow}
                    className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                  >
                    Continue →
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Welcome Modal */}
      <WelcomeModal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} clientName={clientName} />
    </div>
  );
};

export default StatTakers;
